import { useState } from 'react';
import { Trash2, Pencil, Check, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type { Id } from '../../../convex/_generated/dataModel'; // DEEP_IMPORT_JUSTIFIED: Convex generated dataModel lives outside src/
import type { IMemberListProps } from '@/@types/organisation';

function formatDate(timestamp: number): string {
  return new Date(timestamp).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export function MemberList({
  members,
  isOwner,
  currentUserId,
  onRemoveMember,
  onUpdateName,
  isRemoving,
}: IMemberListProps) {
  const [editingUserId, setEditingUserId] = useState<Id<'users'> | null>(null);
  const [editingName, setEditingName] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const startEditing = (userId: Id<'users'>, currentName: string) => {
    setEditingUserId(userId);
    setEditingName(currentName);
  };

  const cancelEditing = () => {
    setEditingUserId(null);
    setEditingName('');
  };

  const handleSave = async () => {
    if (!editingUserId || !editingName.trim()) return;
    setIsSaving(true);
    try {
      await onUpdateName(editingUserId, editingName.trim());
      cancelEditing();
    } finally {
      setIsSaving(false);
    }
  };

  if (members.length === 0) {
    return (
      <div className="rounded-lg border p-8 text-center text-muted-foreground">
        No members found.
      </div>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Name</TableHead>
          <TableHead>Email</TableHead>
          <TableHead>Role</TableHead>
          <TableHead>Joined</TableHead>
          {isOwner && (
            <TableHead className="w-16">
              <span className="sr-only">Actions</span>
            </TableHead>
          )}
        </TableRow>
      </TableHeader>
      <TableBody>
        {members.map(member => {
          const isCurrentUser = currentUserId === member.userId;
          const canEdit = isOwner || isCurrentUser;
          const isEditingThis = editingUserId === member.userId;

          return (
            <TableRow key={member._id}>
              <TableCell className="font-medium">
                {isEditingThis ? (
                  <div className="flex items-center gap-1">
                    <Input
                      value={editingName}
                      onChange={e => setEditingName(e.target.value)}
                      onKeyDown={e => {
                        if (e.key === 'Enter') void handleSave();
                        if (e.key === 'Escape') cancelEditing();
                      }}
                      className="h-8 w-48 text-sm"
                      maxLength={100}
                      autoFocus
                      aria-label={`Edit name for ${member.userEmail ?? 'member'}`}
                    />
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-8 w-8 shrink-0"
                      onClick={() => void handleSave()}
                      disabled={isSaving || !editingName.trim()}
                      aria-label="Save name"
                    >
                      <Check className="h-4 w-4" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-8 w-8 shrink-0"
                      onClick={cancelEditing}
                      aria-label="Cancel editing"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center gap-1.5">
                    {member.userName ?? (
                      <span className="text-muted-foreground italic">Unknown</span>
                    )}
                    {canEdit && (
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-6 w-6"
                        onClick={() => startEditing(member.userId, member.userName ?? '')}
                        aria-label={`Edit name for ${member.userName ?? 'member'}`}
                      >
                        <Pencil className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                )}
              </TableCell>
              <TableCell className="text-muted-foreground">{member.userEmail ?? '—'}</TableCell>
              <TableCell>
                <Badge variant={member.role === 'owner' ? 'default' : 'secondary'}>
                  {member.role === 'owner' ? 'Owner' : 'Member'}
                </Badge>
              </TableCell>
              <TableCell className="text-muted-foreground">{formatDate(member.joinedAt)}</TableCell>
              {isOwner && (
                <TableCell>
                  {member.role !== 'owner' && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => onRemoveMember(member._id)}
                      disabled={isRemoving}
                      className="text-destructive hover:text-destructive"
                      aria-label={`Remove ${member.userName ?? 'member'}`}
                    >
                      <Trash2 aria-hidden="true" className="h-4 w-4" />
                    </Button>
                  )}
                </TableCell>
              )}
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}
